import { useRef, useState } from "react";
import ToggleSwitch from "../BaseComponents/Toggle/ToggleSwitch";
import styles from "./FooterPaymentIcons.module.css";
import { Plus, Trash2, GripVertical, Upload, X } from "lucide-react";
import { useUploadFooterBrandLogo } from "../../api/layout/useFooterConfigApi";
import { useToast } from "../../context/ToastContext";

// What the storage endpoint accepts. Checked here so an unsupported file is
// refused instantly with a reason, rather than after a round trip.
const ACCEPTED = ["image/png", "image/jpeg", "image/webp", "image/svg+xml", "image/gif"];
const MAX_BYTES = 2 * 1024 * 1024;

const FooterPaymentIcons = ({ icons, setIcons }) => {
  const { showToast } = useToast();
  // Same endpoint the brand logo uses: it stores an image in the footer folder
  // and hands back its URL. Payment icons need exactly that, so there is no
  // second upload path to keep in step with this one.
  const { uploadLogo } = useUploadFooterBrandLogo();
  // One hidden input per row, so a click on the visible button opens the picker
  // for THAT icon and no other.
  const fileInputs = useRef({});
  const [uploadingIndex, setUploadingIndex] = useState(null);

  const addIcon = () => {
    setIcons(prev => [
      ...prev,
      { label: "New Payment", active: true, imageUrl: "" }
    ]);
  };

  const toggleIcon = (index) => {
    setIcons(prev =>
      prev.map((icon, i) => (i === index ? { ...icon, active: !icon.active } : icon))
    );
  };

  const updateIconLabel = (index, label) => {
    setIcons(prev =>
      prev.map((icon, i) => (i === index ? { ...icon, label } : icon))
    );
  };

  const setIconImage = (index, imageUrl) => {
    setIcons(prev =>
      prev.map((icon, i) => (i === index ? { ...icon, imageUrl } : icon))
    );
  };

  const deleteIcon = (index) => {
    setIcons(prev => prev.filter((_, i) => i !== index));
  };

  /**
   * Upload the chosen file and hang its URL on this icon.
   *
   * The image is stored immediately, but the icon only points at it once the
   * whole footer is saved. That matches every other field on this screen and
   * means an abandoned edit leaves an unused file rather than a broken icon.
   */
  const handleFile = async (index, file) => {
    if (!file) return;
    if (!ACCEPTED.includes(file.type)) {
      showToast("Choose a PNG, JPG, WEBP, SVG or GIF image", "error");
      return;
    }
    if (file.size > MAX_BYTES) {
      showToast("That image is over 2 MB. Please use a smaller file.", "error");
      return;
    }

    setUploadingIndex(index);
    try {
      const url = await uploadLogo(file);
      if (!url) throw new Error("No image URL returned");
      setIconImage(index, url);
      showToast("Icon uploaded. Save the footer to publish it.", "success");
    } catch (err) {
      showToast(
        err?.response?.data?.message || err?.message || "Could not upload the icon",
        "error"
      );
    } finally {
      setUploadingIndex(null);
      // Clear the input so picking the SAME file again still fires onChange.
      if (fileInputs.current[index]) fileInputs.current[index].value = "";
    }
  };

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div>
          <h3>Payment Icons</h3>
          <p>Supported payment method icons</p>
        </div>

        <button type="button" className={styles.addBtn} onClick={addIcon}>
          <Plus size={14} /> Add Payment
        </button>
      </div>

      <div className={styles.iconList}>
        {icons.map((icon, index) => (
          <div key={index} className={styles.row}>
            <div className={styles.drag}>
              <GripVertical size={14} />
              <span>{index + 1}</span>
            </div>

            <div className={styles.uploadBox}>
              {/* The button used to be inert: no input, no handler, so nothing
                  happened on click and no icon could ever be uploaded. */}
              <input
                ref={(el) => { fileInputs.current[index] = el; }}
                type="file"
                accept={ACCEPTED.join(",")}
                className={styles.fileInput}
                onChange={(e) => handleFile(index, e.target.files?.[0])}
              />

              {icon.imageUrl ? (
                <div className={styles.preview}>
                  <img src={icon.imageUrl} alt={icon.label || "Payment icon"} />
                  <button
                    type="button"
                    className={styles.removeImg}
                    title="Remove this icon image"
                    onClick={() => setIconImage(index, "")}
                  >
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <Upload size={16} />
              )}

              <button
                type="button"
                className={styles.uploadBtn}
                disabled={uploadingIndex === index}
                onClick={() => fileInputs.current[index]?.click()}
              >
                {uploadingIndex === index
                  ? "Uploading…"
                  : icon.imageUrl
                    ? "Replace"
                    : "Upload"}
              </button>
            </div>

            <input
              className={styles.input}
              value={icon.label}
              onChange={e => updateIconLabel(index, e.target.value)}
              placeholder="Payment label"
            />

            <ToggleSwitch
              checked={icon.active}
              onChange={() => toggleIcon(index)}
            />

            <button
              type="button"
              className={styles.deleteBtn}
              onClick={() => deleteIcon(index)}
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FooterPaymentIcons;
